import { FC, ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, Link, Typography } from '@robocorp/components';
import { IconLogoRobocorp } from '@robocorp/icons/logos';

import { ExternalLinks, LAST_BREAKING_TOS_CHANGES } from '~/constants';
import { useIsSSO, useLogout } from '~/hooks';
import { useUserAcceptToSMutation, useCurrentUser } from '~/queries';
import ViewLoader from './ViewLoader';

type Props = {
  children?: ReactNode;
};

const ToSAcceptance: FC = () => {
  const onLogout = useLogout();
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);

  const { mutateAsync: acceptToS, isLoading } = useUserAcceptToSMutation();

  const onAcceptToS = useCallback(() => {
    acceptToS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      pt="$64"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="space-between"
      height="100%"
      gap="$48"
    >
      <IconLogoRobocorp size={48} />
      <Box maxWidth={720} px="$56">
        <Typography variant="display.large" mb="$24" textAlign="center">
          {t('termsOfService.updateTitle')}
        </Typography>
        <Typography mb="$24" fontSize="$14" lineHeight="$28" textAlign="justify">
          {t('termsOfService.updateDescription')}
        </Typography>
        <Box mb="$48">
          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            label={
              <>
                <Typography fontWeight="medium" mr="$4">
                  {t('termsOfService.agreeLabel')}
                </Typography>
                <Link fontWeight="medium" href={ExternalLinks.TERMS_OF_USE} target="_blank">
                  {t('termsOfService.title')}
                </Link>
              </>
            }
          />
        </Box>
        <Box display="flex" justifyContent="flex-end" gap="$8">
          <Button variant="secondary" onClick={onLogout} disabled={isLoading}>
            {t('logout')}
          </Button>
          <Button disabled={!agreed} onClick={onAcceptToS} loading={isLoading}>
            {t('continue')}
          </Button>
        </Box>
      </Box>
      <Box pb="$36">
        ©️ {new Date().getFullYear()} Robocorp{' '}
        <Box ml="$16" display="inline-flex" gap="$4">
          <Link href={ExternalLinks.TERMS_OF_USE} variant="subtle" target="_blank">
            {t('termsOfService.title')}
          </Link>
          ·
          <Link href={ExternalLinks.PRIVACY_POLICY} variant="subtle" target="_blank">
            {t('privacyPolicy')}
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * This view checks if user has agreed to the latest LAST_BREAKING_TOS_CHANGES
 * and if not, blocks with a view to accept them
 */
export const ToSGuard: FC<Props> = ({ children }) => {
  const { isSSO } = useIsSSO();
  const { data: currentUser, isLoading } = useCurrentUser();

  if (isLoading) {
    return <ViewLoader />;
  }

  if (!isSSO && currentUser) {
    const shouldDisplayToSAcceptance =
      LAST_BREAKING_TOS_CHANGES <= new Date().getTime() &&
      (!currentUser.details.tos_accepted_ts ||
        new Date(currentUser.details.tos_accepted_ts).getTime() < LAST_BREAKING_TOS_CHANGES);

    if (shouldDisplayToSAcceptance) {
      return <ToSAcceptance />;
    }
  }

  return <>{children}</>;
};
